import { useRef, useState } from "react";
import { useNativeDialog } from "../../components/useNativeDialog";
import { ApiError } from "../../lib/api-client";
import { deleteWish } from "../../lib/api-wish";
import type { OwnerWish } from "../../types/wish";

interface DeleteWishConfirmProps {
  wish: OwnerWish;
  onClose: () => void;
  onDeleted: (id: string) => void;
}

function deleteError(error: unknown): string {
  if (error instanceof ApiError && error.code === "FORBIDDEN") {
    return "Нельзя удалить чужое желание";
  }
  if (error instanceof ApiError && error.code === "NOT_FOUND") {
    return "Желание уже удалено. Обновите список.";
  }
  return "Не удалось удалить желание. Попробуйте ещё раз.";
}

export function DeleteWishConfirm({ wish, onClose, onDeleted }: DeleteWishConfirmProps) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const modal = useNativeDialog(deleting, onClose, confirmRef);

  async function confirmDelete() {
    setDeleting(true);
    setError(null);
    try {
      const result = await deleteWish(wish.id);
      onDeleted(result.id);
    } catch (mutationFailure) {
      setError(deleteError(mutationFailure));
      setDeleting(false);
    }
  }

  return (
    <dialog
      ref={modal.ref}
      onCancel={modal.onCancel}
      onKeyDown={modal.onKeyDown}
      aria-labelledby="delete-wish-title"
      aria-describedby="delete-wish-description"
      className="m-auto w-[calc(100%_-_2rem)] max-w-sm rounded-[28px] border-0 bg-white p-6 text-center text-content shadow-2xl backdrop:bg-black/40"
    >
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-danger-soft text-3xl font-bold text-danger">
        {deleting ? <span className="h-7 w-7 animate-spin rounded-full border-[3px] border-danger border-t-transparent" /> : "!"}
      </div>
      <h2 id="delete-wish-title" className="mt-5 text-2xl font-extrabold tracking-[-0.04em] text-[#171714]">
        {deleting ? "Удаляем…" : "Удалить желание?"}
      </h2>
      <p id="delete-wish-description" className="mt-2 text-sm leading-6 text-muted">
        {deleting ? "Пожалуйста, подождите" : <>Действие нельзя отменить. «{wish.title}» будет удалено.</>}
      </p>
      {error && <div role="alert" className="mt-4 rounded-2xl bg-danger-soft px-4 py-3 text-sm font-semibold text-danger">{error}</div>}
      <div className="mt-6 flex flex-col gap-3">
        <button ref={confirmRef} type="button" onClick={confirmDelete} disabled={deleting} className="h-12 cursor-pointer rounded-xl bg-danger text-sm font-bold text-white disabled:cursor-not-allowed disabled:bg-[#FCA5A5]">
          {deleting ? "Удаляем…" : "Удалить"}
        </button>
        <button type="button" onClick={onClose} disabled={deleting} className="h-12 cursor-pointer rounded-xl border border-line text-sm font-bold text-content disabled:cursor-not-allowed">
          Отмена
        </button>
      </div>
    </dialog>
  );
}
